import React, { useEffect, useMemo, useState } from 'react'
import { useLoad } from '../shared/hooks/load'
import { useQueryParam } from '../shared/hooks/location'
import { Card } from '../shared/card/card'
import { useApiFormAuthProvider } from '../api-new/state/auth'
import { ButtonPrimary } from '../shared/component/button'
import { SignInLinkFailed, SignInLinkSpent, useSignInLinkSpent } from '../shared/sso-workspace/sign-in-link-spent'

// /open/complete?token=…: the sign-in is done; on to the client's sign-in
// URL (redirect-signin-url: the admin's /open/auth, an app's edxr://auth
// deep link, or the web player's own page). The hand-off replaces this page
// in history (owner report 2026-10-13): Back or a restored tab must not come
// back to a token the app has already used. If it does anyway, the details
// call answers sign_in_link_used and the calm SignInLinkSpent card shows (or,
// with a session here, the admin). The Retry button is the person's own tap.
//
// A web return (http/https: the admin itself, or the web player, which signs
// in in its own tab since the client report of 2026-10-21) never says
// "returned to the app" or "close this window": the person is in the same
// tab, being taken back. Until the details answer, the page says only
// "Signing you in…", since it can't yet tell an app from a web page.
const isWebReturn = url => !!url && (url.protocol === 'https:' || url.protocol === 'http:')

export const OpenComplete = () => {
  const token = useQueryParam('token')

  const [error, updateError] = useState()

  const [hasRedirect, updateHasRedirect] = useState(false)

  const apiFormAuthProvider = useApiFormAuthProvider()

  const [spent, onSpent] = useSignInLinkSpent()
  // The link's first call failed otherwise: the "didn't work" card (owner 2026-10-17).
  const [failed, updateFailed] = useState(null)

  const [loaded, updateLoaded] = useState(false)

  useLoad(async () => {
    try {
      await apiFormAuthProvider.get(`${token}/details`)
      updateLoaded(true)
    } catch (error) {
      if (!onSpent(error)) updateFailed(error || {})
    }
  }, [])

  let url = useMemo(
    () => {
      if (apiFormAuthProvider.form.redirectSigninURL) {
        let url = new URL(apiFormAuthProvider.form.redirectSigninURL)
        url.searchParams.set('token', token)

        updateHasRedirect(true)

        return url
      }

      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiFormAuthProvider]
  )

  useEffect(
    () => {
      if (url) {
        try {
          window.location.replace(url.href)
        } catch (error) {
          updateError(error)
        }
      }
    },
    [url]
  )

  const handleOpen = () => {
    window.location = url.href
  }

  if (spent) {
    return <SignInLinkSpent reason={spent} />
  }

  if (failed) {
    return <SignInLinkFailed error={failed} />
  }

  if (isWebReturn(url)) {
    return (
      <Card>
        <div>
          <p>Signing you in…</p>
          {error
            ? <div className="border-top pt-3">
                <p>We couldn't take you back to EducationXR automatically.</p>
                <ButtonPrimary onClick={handleOpen}>Continue</ButtonPrimary>
              </div>
            : <p>Taking you back to EducationXR…</p>}
        </div>
      </Card>
    )
  }

  if (!hasRedirect && !loaded) {
    return (
      <Card>
        <div className="pt-3">
          <p>Signing you in…</p>
        </div>
      </Card>
    )
  }

  return (
    <React.Fragment>
      {hasRedirect
        ? <Card>
            <div>
              <p>Login successful, you are now being returned to the app…</p>
              <p>If for some reason you are not automatically redirected close this window and return to the app to finish signing in.</p>

              <div>
                {error &&
                  <div className="border-top pt-3">
                    <p>An error occurred opening the EducationXR App from your browser.</p>
                    <ButtonPrimary onClick={handleOpen}>Retry</ButtonPrimary>
                  </div>}
              </div>
            </div>
          </Card>
        : <Card>
            <div className="pt-3">
              <p>Login successful, please close this window and return to the app to complete the sign in.</p>
            </div>
          </Card>}
    </React.Fragment>
  )
}
